import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { generateInviteCode, incrementInviteCodeUsage } from "./inviteCodeService";

export const migrateDataToOrganization = async ({
  db,
  appId,
  uid,
  orgId,
  collectionsToMigrate = ["students", "teachers", "schedules", "finances", "visitLogs", "meetingMinutes"],
  continueOnCollectionError = false,
}) => {
  const batch = writeBatch(db);
  let migratedCount = 0;

  try {
    for (const colName of collectionsToMigrate) {
      if (continueOnCollectionError) {
        try {
          const q = query(collection(db, colName), where("userId", "==", uid));
          const snapshot = await getDocs(q);

          snapshot.docs.forEach((docSnap) => {
            const docRef = doc(db, colName, docSnap.id);
            batch.update(docRef, { organizationId: orgId });
            migratedCount++;
          });
        } catch (e) {
          console.log(`Collection ${colName} migration skipped:`, e.message);
        }
      } else {
        const q = query(collection(db, colName), where("userId", "==", uid));
        const snapshot = await getDocs(q);

        snapshot.docs.forEach((docSnap) => {
          const docRef = doc(db, colName, docSnap.id);
          batch.update(docRef, { organizationId: orgId });
          migratedCount++;
        });
      }
    }

    if (continueOnCollectionError) {
      try {
        const oldAttendanceRef = collection(db, "artifacts", appId, "users", uid, "attendance");
        const attendanceSnapshot = await getDocs(oldAttendanceRef);

        attendanceSnapshot.docs.forEach((docSnap) => {
          const dateId = docSnap.id;
          const newAttendanceRef = doc(db, "organizations", orgId, "attendance", dateId);
          batch.set(newAttendanceRef, docSnap.data(), { merge: true });
          migratedCount++;
        });
      } catch (e) {
        console.log("Attendance migration skipped:", e.message);
      }
    } else {
      const oldAttendanceRef = collection(db, "artifacts", appId, "users", uid, "attendance");
      const attendanceSnapshot = await getDocs(oldAttendanceRef);

      attendanceSnapshot.docs.forEach((docSnap) => {
        const dateId = docSnap.id;
        const newAttendanceRef = doc(db, "organizations", orgId, "attendance", dateId);
        batch.set(newAttendanceRef, docSnap.data(), { merge: true });
        migratedCount++;
      });
    }

    if (migratedCount > 0) {
      await batch.commit();
      if (continueOnCollectionError) {
        console.log(`✅ ${migratedCount}개의 기존 데이터 마이그레이션 완료`);
      } else {
        console.log(`✅ ${migratedCount}개의 데이터 마이그레이션 완료`);
      }
    }
  } catch (err) {
    console.error("Migration failed:", err);
  }
};

export const createOrganizationWithInviteCodes = async ({ db, userUid, churchName, deptName }) => {
  const orgId = `org_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  await setDoc(doc(db, "organizations", orgId), {
    id: orgId,
    name: churchName,
    defaultDepartment: deptName,
    createdBy: userUid,
    createdAt: serverTimestamp(),
    memberCount: 1,
  });

  const adminCode = generateInviteCode("ADMIN");
  const teacherCode = generateInviteCode("TEACH");

  console.log("🔑 Generated Admin Code:", adminCode);
  console.log("🔑 Generated Teacher Code:", teacherCode);

  await setDoc(doc(db, "inviteCodes", adminCode), {
    code: adminCode,
    organizationId: orgId,
    departmentId: deptName,
    role: "admin",
    createdBy: userUid,
    createdAt: serverTimestamp(),
    usedCount: 0,
    maxUses: 10,
    expiresAt: null,
  });
  console.log("✅ Admin code saved to Firestore");

  await setDoc(doc(db, "inviteCodes", teacherCode), {
    code: teacherCode,
    organizationId: orgId,
    departmentId: deptName,
    role: "teacher",
    createdBy: userUid,
    createdAt: serverTimestamp(),
    usedCount: 0,
    maxUses: 50,
    expiresAt: null,
  });
  console.log("✅ Teacher code saved to Firestore");

  return { orgId, adminCode, teacherCode };
};

export const updateUserProfileForOrgCreation = async ({
  db,
  userUid,
  userName,
  userRole,
  churchName,
  deptName,
  orgId,
  adminCode,
  teacherCode,
}) => {
  await updateDoc(doc(db, "users", userUid), {
    name: userName,
    role: userRole,
    churchName,
    deptName,
    organizationId: orgId,
    memberRole: "admin",
    assignedDepartments: [deptName],
    adminInviteCode: adminCode,
    teacherInviteCode: teacherCode,
    departmentCodes: {
      [deptName]: { admin: adminCode, teacher: teacherCode },
    },
    setupComplete: true,
  });
};

export const joinOrganization = async ({
  db,
  userUid,
  userName,
  userRole,
  validatedCodeData,
}) => {
  const { organizationId, orgName, deptName, role, code } = validatedCodeData;

  await updateDoc(doc(db, "users", userUid), {
    name: userName,
    role: userRole,
    organizationId,
    churchName: orgName,
    deptName,
    memberRole: role,
    assignedDepartments: [deptName],
    joinedAt: serverTimestamp(),
    setupComplete: true,
  });

  await incrementInviteCodeUsage(db, code);

  const orgRef = doc(db, "organizations", organizationId);
  const orgSnapshot = await getDoc(orgRef);
  const memberCount = orgSnapshot.data()?.memberCount || 0;

  await updateDoc(orgRef, {
    memberCount: memberCount + 1,
  });
};

export const joinOrganizationFromInviteCodeForProfile = async ({ db, userUid, code }) => {
  const codeToCheck = code.toUpperCase().trim();
  console.log("🔍 Looking for invite code:", codeToCheck);

  const codeDoc = await getDoc(doc(db, "inviteCodes", codeToCheck));
  console.log("📄 Code doc exists:", codeDoc.exists());

  if (!codeDoc.exists()) {
    throw new Error("유효하지 않은 초대 코드입니다.");
  }

  const codeData = codeDoc.data();

  if (codeData.maxUses && codeData.usedCount >= codeData.maxUses) {
    throw new Error("이 초대 코드는 더 이상 사용할 수 없습니다.");
  }

  const orgDoc = await getDoc(doc(db, "organizations", codeData.organizationId));
  if (!orgDoc.exists()) {
    throw new Error("조직을 찾을 수 없습니다.");
  }

  const orgData = orgDoc.data();

  await updateDoc(doc(db, "users", userUid), {
    organizationId: codeData.organizationId,
    churchName: orgData.name,
    deptName: orgData.defaultDepartment,
    memberRole: codeData.role,
    joinedAt: serverTimestamp(),
    setupComplete: true,
  });

  await updateDoc(doc(db, "inviteCodes", codeToCheck), {
    usedCount: (codeData.usedCount || 0) + 1,
  });

  await updateDoc(doc(db, "organizations", codeData.organizationId), {
    memberCount: (orgData.memberCount || 1) + 1,
  });
};

export const createOrganizationFromProfile = async ({ db, appId, userUid, churchName, deptName }) => {
  const orgId = `org_${userUid}_${Date.now()}`;
  const trimmedChurchName = churchName.trim();
  const trimmedDeptName = deptName.trim();

  const adminCode = generateInviteCode("ADMIN");
  const teacherCode = generateInviteCode("TEACH");

  await setDoc(doc(db, "organizations", orgId), {
    name: trimmedChurchName,
    defaultDepartment: trimmedDeptName,
    createdBy: userUid,
    createdAt: serverTimestamp(),
    memberCount: 1,
    appId,
  });

  await setDoc(doc(db, "inviteCodes", adminCode), {
    organizationId: orgId,
    role: "admin",
    department: trimmedDeptName,
    usedCount: 0,
    createdAt: serverTimestamp(),
  });

  await setDoc(doc(db, "inviteCodes", teacherCode), {
    organizationId: orgId,
    role: "teacher",
    department: trimmedDeptName,
    usedCount: 0,
    createdAt: serverTimestamp(),
  });

  await migrateDataToOrganization({
    db,
    appId,
    uid: userUid,
    orgId,
    collectionsToMigrate: ["students", "teachers", "schedules", "finances", "visitLogs", "meetingMinutes", "counselingLogs"],
    continueOnCollectionError: true,
  });

  await updateDoc(doc(db, "users", userUid), {
    organizationId: orgId,
    churchName: trimmedChurchName,
    deptName: trimmedDeptName,
    memberRole: "admin",
    setupComplete: true,
    departmentCodes: {
      [trimmedDeptName]: {
        admin: adminCode,
        teacher: teacherCode,
      },
    },
  });
};
